import { AnimatePresence, motion } from "framer-motion";
import { useContext, useState } from "react";
import { MdClose } from "react-icons/md";
import StepElement from "./utils/StepElement";
import UploadCsv from "./UploadCsv";
import CheckFormat from "./CheckFormat";
import { ClassImportListType, StudentImportListType } from "utils/interfaces";
import {
  checkRecordsExistence,
  createRecordRequest,
  deleteRecordRequest,
} from "services/apiRequests";
import ReviewImport from "./ReviewImport";
import ImportComplete from "./ImportComplete";
import { logoutFunction } from "services/authRequests";
import { MainContext } from "components/utils/Contexts";

interface ImportStudentsPopUpProps {
  setShowPopUp: React.Dispatch<React.SetStateAction<boolean>>;
  onCreateEditStudent: (title?: string, description?: string) => void;
  afterImportFunction: () => void;
}

const ImportStudentsPopUp: React.FC<ImportStudentsPopUpProps> = ({
  setShowPopUp,
  onCreateEditStudent,
  afterImportFunction,
}) => {
  // States definition
  const [stage, setStage] = useState(1);
  const [file, setFile] = useState<string[]>([]);
  const [classes, setClasses] = useState<ClassImportListType[]>([]);
  const [students, setStudents] = useState<StudentImportListType[]>([]);
  const [csvErrors, setCsvErrors] = useState<string[][]>([]);
  const [checkFormatState, setCheckFormatState] = useState<
    "import" | "processing" | "warning" | "missing-fields"
  >("import");
  const [reviewImportState, setReviewImportState] = useState<
    "submit" | "processing"
  >("submit");
  // State used for handling the import option. By default it's just
  // creating all the defined elements
  const [importOption, setImportOption] = useState<
    "create" | "replace" | "update"
  >("create");
  // Specific states for classes and students to be created and to be updated/replaced
  const [newStudents, setNewStudents] = useState<StudentImportListType[]>([]);
  const [existingStudents, setExistingStudents] = useState<
    StudentImportListType[]
  >([]);
  const [otherSchoolStudents, setOtherSchoolStudents] = useState<
    StudentImportListType[]
  >([]);
  const [newClasses, setNewClasses] = useState<ClassImportListType[]>([]);
  const [existingClasses, setExistingClasses] = useState<ClassImportListType[]>(
    []
  );

  // Specific state to track the id of completed import incase of discarding
  const [importRequestId, setImportRequestId] = useState<String | undefined>(undefined);

  const [isMissingFields, setIsMissingFields] = useState(true);

  const { contextSchoolByRole } = useContext(MainContext);

  /**
   * Function used on pressing "Click here" on
   * the stage 1 of the upload csv flow
   */
  const onImportIndividualStudent = () => {
    // Go into the create new view
    onCreateEditStudent();

    // And close this pop up
    setShowPopUp(false);
  };

  /**
   * Function used to handle the behaviour of the back
   * button based on the current stage
   */
  const handleBackButton = () => {
    if (stage === 2) {
      comeBackToStage1();
    } else if (stage === 3) {
      comeBackToStage2();
    }
  };

  /**
   * Auxiliar functions
   */
  const comeBackToStage1 = () => {
    // Force empty the file
    setFile([]);
    // Force clean the states as well
    setStudents([]);
    setClasses([]);
    setCsvErrors([]);
    setNewStudents([]);
    setExistingStudents([]);
    setImportRequestId(undefined);
    // Force reset importing data
    setCheckFormatState("import");
    // And come back to the first stage
    setStage(1);
  };

  const comeBackToStage2 = () => {
    // Force reset importing data
    setCheckFormatState("import");
    // And come back to the first stage
    setStage(2);
  };

  /**
   * Function used to handle the sending of the users and
   */
  const handleReviewAndImport = async () => {
    // Set state of importingData
    setCheckFormatState("processing");

    // Check if the classes already exist (classes is a list of names)
    const classesCheckResponse = await checkRecordsExistence(
      { classes },
      `/api/classes/exists?schoolId=${contextSchoolByRole && contextSchoolByRole._id}`
    );

    // Check if we got a response
    if (!classesCheckResponse) {
      return;
    }

    // Check if necessary to logout
    if (classesCheckResponse.logout) {
      return await logoutFunction();
    }

    // Check if the response went right
    if (!classesCheckResponse.successful) {
      setCheckFormatState("import");
      return;
    }

    // Check if the students already exist
    const studentsCheckResponse = await checkRecordsExistence(
      { students: students },
      "/api/users/students/exists"
    );

    // Check if the response went right
    if (!(studentsCheckResponse && studentsCheckResponse.successful)) {
      setCheckFormatState("import");
      return;
    }

    // Get the values
    const { newStudents, existingStudents, otherSchoolStudents } =
      studentsCheckResponse.content;
    const { newClasses, existingClasses } = classesCheckResponse.content;

    // Update the states
    setNewStudents(newStudents);
    setExistingStudents(existingStudents);
    setOtherSchoolStudents(otherSchoolStudents);
    setNewClasses(newClasses);
    setExistingClasses(existingClasses);

    // Check if some of the classes already exist in the database
    if (existingStudents.length > 0 || existingClasses.length > 0) {
      // If something exists, go to the warning view
      setCheckFormatState("warning");
    } else {
      // Otherwise, go to the stage 3 (Review & Import)
      setStage(3);
    }
  };

  /**
   * Function used to handle the import process
   */
  const handleCompleteImport = async () => {
    // First of all, change the view
    setReviewImportState("processing");

    // We will start importing the classes and students (creating the new
    // and updating/replacing the existing ones)
    const response = await createRecordRequest(
      {
        organisation: contextSchoolByRole._id,
        newClasses,
        existingClasses,
        newStudents,
        existingStudents,
        importOption,
      },
      "/api/users/students/import"
    );

    // Check if the response went right
    if (!(response && response.successful)) {
      setReviewImportState("submit");
      return;
    }

    // Otherwise, keep the results
    const content = response.content;

    // And set their states
    setImportRequestId(content.importRequestId);

    // And go to the stage 4 (Import complete)
    setStage(4);
  };

  /**
   * Function executed when decide to discard import
   * @returns
   */
  const handleDiscardAllChanges = async () => {
    // Send the discard request
    const response = await deleteRecordRequest(
      { importRequestId },
      "/api/users/students/import"
    );

    // Check if the response was right
    if (!response.successful) return;

    // And come back to the first stage resetting all the states
    setStage(1);
    setFile([]);
    setClasses([]);
    setStudents([]);
    setCsvErrors([]);
    setCheckFormatState("import");
    setReviewImportState("submit");
    setImportOption("create");
    setNewStudents([]);
    setNewClasses([]);
    setExistingClasses([]);
  };

  /**
   * Function executed when clicking finishing process button
   */
  const handleFinishAndBack = () => {
    // Execute the after import function
    afterImportFunction();

    // Close the pop up window
    setShowPopUp(false);
  };

  return (
    <motion.div
      className="fixed inset-0 w-full h-full p-5 bg-neutral-500 bg-opacity-40 backdrop-blur-[2px] inline-flex items-end justify-center z-20"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{
        opacity: 0,
        transition: { duration: 0.1, ease: "easeOut" },
      }}
      transition={{ duration: 0.15, ease: "easeOut" }}
    >
      <div
        className="absolute w-full h-full z-20"
        onClick={() => setShowPopUp(false)}
      />
      <div className="relative w-fit h-fit max-w-[605px] max-h-[90vh] p-6 bg-white rounded-2xl flex-col justify-start items-start gap-12 inline-flex z-30">
        <div className="self-stretch justify-between items-center inline-flex">
          <div className="text-neutral-600 text-[19px] font-semibold font-sans leading-[23px] select-none">
            Bulk Import students
          </div>
          <div
            className="p-2 bg-neutral-50 hover:bg-neutral-100 ease-in-out duration-150 rounded-full justify-start items-start gap-2 flex cursor-pointer"
            onClick={() => setShowPopUp(false)}
          >
            <div className="w-4 h-4 relative">
              <MdClose className="w-full h-full fill-neutral-500" />
            </div>
          </div>
        </div>
        <div className="self-stretch justify-start items-start gap-6 inline-flex overflow-hidden">
          <div className="self-stretch flex-col flex-0 justify-start items-center gap-3 inline-flex">
            <StepElement number={1} text="Upload CSV" currentStage={stage} />
            <div
              className={`h-full border border-neutral-500 ${
                stage > 1 ? "border-solid" : "border-dashed"
              }`}
            />
            <StepElement number={2} text="Check format" currentStage={stage} />
            <div
              className={`h-full border border-neutral-500 ${
                stage > 2 ? "border-solid" : "border-dashed"
              }`}
            />
            <StepElement
              number={3}
              text="Review & Import"
              currentStage={stage}
            />
          </div>
          <AnimatePresence mode="wait">
            {stage === 1 ? (
              <UploadCsv
                key="stage-1"
                file={file}
                setFile={setFile}
                setStage={setStage}
                onImportIndividualStudent={onImportIndividualStudent}
              />
            ) : stage === 2 ? (
              <CheckFormat
                key="stage-2"
                file={file}
                checkFormatState={checkFormatState}
                students={students}
                comeBackToStage1={comeBackToStage1}
                setStage={setStage}
                setClasses={setClasses}
                setStudents={setStudents}
                setCsvErrors={setCsvErrors}
                setImportOption={setImportOption}
                setIsMissingFields={setIsMissingFields}
              />
            ) : stage === 3 ? (
              <ReviewImport
                key="stage-3"
                importOption={importOption}
                reviewImportState={reviewImportState}
                csvErrors={csvErrors}
                newStudents={newStudents}
                newClasses={newClasses}
                existingStudents={existingStudents}
                existingClasses={existingClasses}
                otherSchoolStudents={otherSchoolStudents}
              />
            ) : stage === 4 ? (
              <ImportComplete
                key="stage-4"
                existingStudents={existingStudents}
              />
            ) : (
              <></>
            )}
          </AnimatePresence>
        </div>
        <div
          className={`self-stretch h-[48px] justify-end items-center gap-8 inline-flex ${
            (stage === 1 ||
              (stage === 2 &&
                (checkFormatState === "processing" ||
                  checkFormatState === "warning")) ||
              (stage === 3 && reviewImportState === "processing")) &&
            "invisible"
          }`}
        >
          {stage <= 3 && (
            <div className="rounded justify-center items-center gap-2 flex">
              <button
                className="text-secondary-600 text-lg font-semibold font-sans leading-snug cursor-pointer hover:underline select-none"
                onClick={handleBackButton}
              >
                Back
              </button>
            </div>
          )}
          {stage === 4 && (
            <div className="rounded justify-center items-center gap-2 flex">
              <button
                className="text-secondary-600 text-lg font-semibold font-sans leading-snug cursor-pointer hover:underline select-none"
                onClick={handleDiscardAllChanges}
              >
                Discard all changes
              </button>
            </div>
          )}
          {stage === 2 && checkFormatState === "import" && !isMissingFields && (
            <button
              className="px-5 py-3 bg-secondary-600 hover:bg-secondary-700 ease-in-out duration-150 rounded justify-center items-center gap-2 flex cursor-pointer"
              onClick={handleReviewAndImport}
            >
              <div className="text-white text-lg font-semibold font-sans leading-snug select-none">
                Proceed to Review and Import
              </div>
            </button>
          )}
          {stage === 3 && reviewImportState === "submit" && (
            <button
              className="px-5 py-3 bg-secondary-600 hover:bg-secondary-700 ease-in-out duration-150 rounded justify-center items-center gap-2 flex cursor-pointer"
              onClick={handleCompleteImport}
            >
              <div className="text-white text-lg font-semibold font-sans leading-snug select-none">
                Complete Bulk Import
              </div>
            </button>
          )}
          {stage === 4 && (
            <button
              className="px-5 py-3 bg-secondary-600 hover:bg-secondary-700 ease-in-out duration-150 rounded justify-center items-center gap-2 flex cursor-pointer"
              onClick={handleFinishAndBack}
            >
              <div className="text-white text-lg font-semibold font-sans leading-snug select-none">
                Finish and back to main screen
              </div>
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};
export default ImportStudentsPopUp;
